import { Link } from 'react-router-dom';

import useGoBack from '@/hooks/useGoBack';
import useTranslations from '@/i18n/useTranslations';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import SuarteName from '@/shared-components/icons/components/public/SuarteName';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import Heading from '@/shared-components/text/components/Heading';
import Text from '@/shared-components/text/components/Text';

import './index.css';

export default function LimitedEditionPolicies() {
  const goBackHandler = useGoBack('/');
  const t = useTranslations('common.limited_editions_policy');

  return (
    <main className="limited-edition-policies__page">
      <StaticSeoHead
        title={t('seo.title')}
        description={t('seo.description')}
        canonicalPath="/limited-edition-policies"
        breadcrumbItems={[
          { name: t('seo.home'), url: 'https://suarte.art/' },
          { name: t('title'), url: 'https://suarte.art/limited-edition-policies' },
        ]}
      />

      <header className="limited-edition-policies__header">
        <BackArrowIcon
          className="limited-edition-policies__back-button"
          onClick={goBackHandler}
        />
        <SuarteName className="limited-edition-policies__brand" />
      </header>

      <nav className="limited-edition-policies__subnav" aria-label={t('navigation.aria_label')}>
        <a href="#overview" className="limited-edition-policies__subnav-link">{t('navigation.overview')}</a>
        <a href="#production" className="limited-edition-policies__subnav-link">{t('navigation.production')}</a>
        <a href="#commercial-terms" className="limited-edition-policies__subnav-link">{t('navigation.commercial_terms')}</a>
        <a href="#eligibility" className="limited-edition-policies__subnav-link">{t('navigation.eligibility')}</a>
        <a href="#availability" className="limited-edition-policies__subnav-link">{t('navigation.availability')}</a>
        <a href="#legal-terms" className="limited-edition-policies__subnav-link">{t('navigation.legal_terms')}</a>
      </nav>

      <header id="overview" className="limited-edition-policies__hero">
        <h1>
          <Heading className="limited-edition-policies__title">
            {t('title')}
          </Heading>
        </h1>
        <Text className="limited-edition-policies__subtitle" paragraph>
          {t('intro')}
        </Text>
      </header>

      <section id="production" className="limited-edition-policies__grid">
        <article className="limited-edition-policies__card">
          <h2><Heading className="limited-edition-policies__section-title">{t('requesting.title')}</Heading></h2>
          <Text className="limited-edition-policies__body" paragraph>
            {t('requesting.body')}
          </Text>
        </article>
        <article className="limited-edition-policies__card">
          <h2><Heading className="limited-edition-policies__section-title">{t('manufacturing.title')}</Heading></h2>
          <Text className="limited-edition-policies__body" paragraph>
            {t('manufacturing.body')}
          </Text>
        </article>
      </section>

      <section id="commercial-terms" className="limited-edition-policies__grid">
        <article className="limited-edition-policies__card">
          <h2><Heading className="limited-edition-policies__section-title">{t('commission.title')}</Heading></h2>
          <Text className="limited-edition-policies__body" paragraph>
            {t('commission.body')}
          </Text>
          <aside className="limited-edition-policies__note" role="note">
            <span className="limited-edition-policies__note-dot" aria-hidden="true" />
            {t('commission.note')}
          </aside>
        </article>
        <article className="limited-edition-policies__card">
          <h2><Heading className="limited-edition-policies__section-title">{t('pricing.title')}</Heading></h2>
          <Text className="limited-edition-policies__body" paragraph>
            {t('pricing.body')}
          </Text>
        </article>
      </section>

      <section className="limited-edition-policies__card">
        <h2><Heading className="limited-edition-policies__section-title">{t('quality.title')}</Heading></h2>
        <Text className="limited-edition-policies__body" paragraph>
          {t('quality.body')}
        </Text>
      </section>

      <section id="eligibility" className="limited-edition-policies__grid">
        <article className="limited-edition-policies__card">
          <h2><Heading className="limited-edition-policies__section-title">{t('requirements.title')}</Heading></h2>
          <ul className="limited-edition-policies__list">
            <li>{t('requirements.items.inventory')}</li>
            <li>{t('requirements.items.dimensions')}</li>
            <li>{t('requirements.items.price')}</li>
            <li>{t('requirements.items.format')}</li>
          </ul>
        </article>
        <article className="limited-edition-policies__card">
          <h2><Heading className="limited-edition-policies__section-title">{t('post_upload.title')}</Heading></h2>
          <Text className="limited-edition-policies__body" paragraph>
            {t('post_upload.body')}
          </Text>
        </article>
      </section>

      <section id="availability" className="limited-edition-policies__card">
        <h2><Heading className="limited-edition-policies__section-title">{t('removal.title')}</Heading></h2>
        <Text className="limited-edition-policies__body" paragraph>
          {t('removal.body')}
        </Text>
      </section>

      <section id="legal-terms" className="limited-edition-policies__card">
        <h2><Heading className="limited-edition-policies__section-title">{t('liability.title')}</Heading></h2>
        <Text className="limited-edition-policies__body" paragraph>
          {t('liability.body')}
        </Text>
        <div className="limited-edition-policies__divider" />
        <h2><Heading className="limited-edition-policies__section-title">{t('disputes.title')}</Heading></h2>
        <Text className="limited-edition-policies__body" paragraph>
          {t('disputes.body')}
        </Text>
        <div className="limited-edition-policies__divider" />
        <h2><Heading className="limited-edition-policies__section-title">{t('returns.title')}</Heading></h2>
        <Text className="limited-edition-policies__body" paragraph>
          {t('returns.before_link')}{' '}
          <Link className="limited-edition-policies__link" to="/limited-editions-return-and-refund-policy" state={{ fromPath: '/limited-edition-policies' }}>
            {t('returns.link')}
          </Link>
        </Text>
        <div className="limited-edition-policies__divider" />
        <h2><Heading className="limited-edition-policies__section-title">{t('termination.title')}</Heading></h2>
        <Text className="limited-edition-policies__body" paragraph>
          {t('termination.body')}
        </Text>
        <Text className="limited-edition-policies__body limited-edition-policies__body--last" paragraph>
          {t('termination.effective')}
        </Text>
      </section>
    </main>
  );
}
